import cloneDeep from 'lodash/cloneDeep'
import {
    normalizeVatNumber,
    normalizeCompanyNumber,
} from '@/validators/ukCompanyIds'
import { normalizeCountry } from '@/services/Companies/registeredCountry'

const isBlank = (v) => v === null || typeof v === 'undefined' || v === ''

// "0.05" (stored fraction) -> 5 (percent, max 2 decimals); non-numeric -> null
const fractionToPercent = (v) => {
    if (isBlank(v)) return null
    const n = Number(v)
    if (!Number.isFinite(n)) return null
    return Math.round(n * 10000) / 100
}

// 5 (percent) -> "0.05" (fraction string, max 4 decimals, no float tail)
const percentToFraction = (v) => {
    if (isBlank(v)) return null
    const n = Number(v)
    if (!Number.isFinite(n)) return null
    return String(Number((n / 100).toFixed(4)))
}

// 15.5 -> "15.50"; 0 -> "0.00"; blank -> null
const toMoneyString = (v) => {
    if (isBlank(v)) return null
    const n = Number(v)
    if (!Number.isFinite(n)) return null
    return n.toFixed(2)
}

class CompaniesFormMapper {
    toEntity(values) {
        const entity = cloneDeep(values)

        // read-only server value (company self-registration onboarding state)
        delete entity.onboarding

        if (
            !Array.isArray(entity.acceptedPaymentMethodList) ||
            entity.acceptedPaymentMethodList.length === 0
        ) {
            entity.acceptedPaymentMethodList = ['stripe']
        }

        if (entity.hideAllRestaurants) {
            entity.hiddenRestaurants = []
        } else {
            entity.hideAllExceptions = []
        }

        // a new company gets its type from the server
        if (entity.type === null) {
            delete entity.type
        }

        // task ordit1/tasks/tasks#193: blank = no override (global setting), 0 is kept
        const gap = entity.preorderPickupGap
        entity.preorderPickupGap =
            gap === undefined || gap === null || gap === ''
                ? null
                : parseInt(gap, 10)

        if (typeof entity.deliveryCompletedTemplate == 'undefined') {
            entity.deliveryCompletedTemplate = null
        }

        if (
            typeof entity.serviceFeePercent === 'number' &&
            isFinite(entity.serviceFeePercent)
        ) {
            entity.serviceFeePercent = entity.serviceFeePercent.toString()
        }

        if (entity.billingInfo) {
            entity.billingInfo.serviceFee = percentToFraction(
                entity.billingInfo.serviceFee
            )
            entity.billingInfo.groupOrderFee = toMoneyString(
                entity.billingInfo.groupOrderFee
            )
        }

        if (!entity.orderConfirmationEmail) entity.orderConfirmationEmail = null
        if (!entity.deliveryCompletedEmail) entity.deliveryCompletedEmail = null
        if (!entity.deliveryDriverDeliveringEmail)
            entity.deliveryDriverDeliveringEmail = null

        // the GO restaurants are edited on the Group Order page's Restaurants view (tasks#222), each
        // change saved at once; the company form never sends them, so a stale form cannot revert one
        delete entity.ccPartnerRelations

        entity.deliveryPriceOverrides.forEach((override) => {
            override.min_order_value =
                override.min_order_value || override.min_order_value === 0
                    ? parseFloat(override.min_order_value)
                    : null
            override.delivery_price =
                override.delivery_price || override.delivery_price === 0
                    ? parseFloat(override.delivery_price)
                    : null
        })
        return entity
    }

    fromEntity(entity) {
        const values = cloneDeep(entity)

        if (values.hideAllRestaurants) {
            values.hiddenRestaurants = []
        } else {
            values.hideAllExceptions = []
        }

        if (typeof values.preorderPickupGap === 'undefined') {
            values.preorderPickupGap = null
        }

        if (!values.billingInfo) {
            values.billingInfo = {}
        }

        // not part of this form (see toEntity)
        delete values.ccPartnerRelations

        values.billingInfo.serviceFee = fractionToPercent(
            values.billingInfo.serviceFee
        )

        // stored spellings of valid values become the checked format (a "GB" VAT prefix, a dropped
        // leading zero, "UK"), so a valid legacy value never blocks the save (tasks#211)
        const info = values.billingInfo
        info.vatNumber = normalizeVatNumber(info.vatNumber)
        info.registrationNumber = normalizeCompanyNumber(
            info.registrationNumber
        )
        info.regAddressCountry = normalizeCountry(info.regAddressCountry)

        // a reactive key for the fee basis select, also when the API leaves the unset value out
        if (typeof values.billingInfo.groupOrderFeeBasis === 'undefined') {
            values.billingInfo.groupOrderFeeBasis = null
        }

        return values
    }
}

export default new CompaniesFormMapper()
